import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import ExportButton from '@/components/ExportButton';
import { PageHeader } from '@/components/PageHeader';
import { Building2, Plus, Trash2, Upload, FileSpreadsheet, CheckCircle, XCircle, Clock, RefreshCw, AlertCircle, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Account types
const ACCOUNT_TYPES = {
  CONTO_CORRENTE: 'Conto Corrente',
  CONTO_DEPOSITO: 'Conto Deposito',
  CONTO_RISPARMIO: 'Conto Risparmio'
};

// Import status
const IMPORT_STATUS = {
  UPLOADED: { label: 'Caricato', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  PROCESSING: { label: 'In elaborazione', color: 'bg-blue-100 text-blue-800', icon: RefreshCw },
  COMPLETED: { label: 'Completato', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  FAILED: { label: 'Errore', color: 'bg-red-100 text-red-800', icon: XCircle }
};

export const BankImport = () => {
  const { token, selectedCondominium } = useAuth();
  const userRole = selectedCondominium?.role || 'RESIDENT';
  const condoId = selectedCondominium?.condominium?.id;
  
  // State
  const [accounts, setAccounts] = useState([]);
  const [imports, setImports] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [isNewAccountOpen, setIsNewAccountOpen] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Form state
  const [accountForm, setAccountForm] = useState({
    bank_name: '',
    account_number: '',
    iban: '',
    description: '',
    account_type: 'CONTO_CORRENTE',
    opening_balance: 0,
    opening_date: ''
  });
  
  const [uploadFile, setUploadFile] = useState(null);

  // ---- Import estratti conto PDF (anteprima, poi conferma) ----
  const PDF_MAX_FILES = 12;
  const PDF_MAX_BYTES = 15 * 1024 * 1024;
  const [isPdfOpen, setIsPdfOpen] = useState(false);
  const [pdfFiles, setPdfFiles] = useState([]);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [pdfResult, setPdfResult] = useState(null);
  const [pdfForza, setPdfForza] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const resetPdf = () => {
    setPdfFiles([]);
    setPdfPreview(null);
    setPdfResult(null);
    setPdfForza(false);
  };

  const handlePdfSelect = (e) => {
    const list = Array.from(e.target.files || []);
    if (list.length > PDF_MAX_FILES) {
      toast.error(`Al massimo ${PDF_MAX_FILES} PDF per volta`);
      e.target.value = '';
      return;
    }
    const storto = list.find((f) => !f.name.toLowerCase().endsWith('.pdf'));
    if (storto) {
      toast.error(`${storto.name} non è un PDF`);
      e.target.value = '';
      return;
    }
    const grande = list.find((f) => f.size > PDF_MAX_BYTES);
    if (grande) {
      toast.error(`${grande.name} supera i 15 MB`);
      e.target.value = '';
      return;
    }
    setPdfFiles(list);
    setPdfPreview(null);
    setPdfResult(null);
    setPdfForza(false);
  };

  const chiamaPdf = async (percorso, files = pdfFiles) => {
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    const res = await fetch(
      `${API_URL}/api/condominiums/${condoId}/bank-accounts/${selectedAccount.id}/import-pdf/${percorso}`,
      { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData }
    );
    let data = null;
    try {
      data = await res.json();
    } catch (err) {
      data = null;
    }
    if (!res.ok) {
      const detail = data && data.detail;
      throw new Error(typeof detail === 'string' ? detail : 'Operazione non riuscita');
    }
    return data;
  };

  const handlePdfAnteprima = async (daFuori) => {
    const files = Array.isArray(daFuori) ? daFuori : pdfFiles;
    if (!files.length || !selectedAccount) return;
    setPdfBusy(true);
    setPdfPreview(null);
    setPdfResult(null);
    try {
      setPdfPreview(await chiamaPdf('anteprima', files));
    } catch (error) {
      toast.error(error.message || 'Errore durante la lettura dei PDF');
    }
    setPdfBusy(false);
  };

  const handlePdfConferma = async () => {
    if (!pdfPreview || !selectedAccount) return;
    setPdfBusy(true);
    try {
      const data = await chiamaPdf(`conferma?forza=${pdfForza ? 'true' : 'false'}`);
      setPdfResult(data);
      if (data.totale_importati > 0) {
        toast.success(`Importati ${data.totale_importati} movimenti da ${data.file_importati} file`);
      } else {
        toast.error('Nessun movimento importato');
      }
      fetchImports();
      fetchAccounts();
    } catch (error) {
      toast.error(error.message || 'Errore durante l\'importazione');
    }
    setPdfBusy(false);
  };

  // «Importa Estratto»: un solo punto d'ingresso. Excel/CSV come prima; uno o piu' PDF passano all'importazione PDF (anteprima, poi conferma).
  const handleEstrattoSelect = (e) => {
    const list = Array.from(e.target.files || []);
    const pdf = list.filter((f) => f.name.toLowerCase().endsWith('.pdf'));
    if (pdf.length === 0) {
      if (list.length > 1) {
        toast.error('Per Excel o CSV scegli un file alla volta');
        e.target.value = '';
        return;
      }
      setUploadFile(list[0] || null);
      return;
    }
    if (pdf.length !== list.length) {
      toast.error('Scegli o file PDF o un file Excel/CSV, non insieme');
      e.target.value = '';
      return;
    }
    if (list.length > PDF_MAX_FILES) {
      toast.error(`Al massimo ${PDF_MAX_FILES} PDF per volta`);
      e.target.value = '';
      return;
    }
    const grande = list.find((f) => f.size > PDF_MAX_BYTES);
    if (grande) {
      toast.error(`${grande.name} supera i 15 MB`);
      e.target.value = '';
      return;
    }
    setUploadFile(null);
    setPdfFiles(list);
    setPdfPreview(null);
    setPdfResult(null);
    setPdfForza(false);
    setIsUploadOpen(false);
    setIsPdfOpen(true);
    handlePdfAnteprima(list);
  };

  const pdfImportabili = pdfPreview ? pdfPreview.file.filter((f) => f.importabile).length : 0;
  const pdfConForza = pdfPreview ? pdfPreview.file.filter((f) => f.importabile_con_forza).length : 0;
  const pdfPuoImportare = !!pdfPreview && !pdfResult && !pdfBusy && (pdfImportabili > 0 || (pdfForza && pdfConForza > 0));

  // Fetch functions
  const fetchAccounts = useCallback(async () => {
    if (!condoId) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/bank-accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAccounts(data);
        if (data.length > 0 && !selectedAccount) {
          setSelectedAccount(data[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
    setLoading(false);
  }, [condoId, token, selectedAccount]);

  const fetchImports = useCallback(async () => {
    if (!condoId || !selectedAccount) return;
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/bank-imports?bank_account_id=${selectedAccount.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) setImports(await res.json());
    } catch (error) {
      console.error('Error fetching imports:', error);
    }
  }, [condoId, token, selectedAccount]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  useEffect(() => {
    if (selectedAccount) {
      fetchImports();
    }
  }, [fetchImports, selectedAccount]);

  // Create account
  const handleCreateAccount = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/bank-accounts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(accountForm)
      });
      if (res.ok) {
        toast.success('Conto bancario creato');
        setIsNewAccountOpen(false);
        resetAccountForm();
        fetchAccounts();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore creazione');
      }
    } catch (error) {
      toast.error('Errore creazione');
    }
  };

  // Upload file
  const handleUpload = async () => {
    if (!uploadFile || !selectedAccount) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadFile);
    
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/bank-imports/upload?bank_account_id=${selectedAccount.id}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        }
      );
      
      if (res.ok) {
        const result = await res.json();
        toast.success(result.message);
        setIsUploadOpen(false);
        setUploadFile(null);
        fetchImports();
        fetchAccounts(); // Refresh balance
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore importazione');
      }
    } catch (error) {
      toast.error('Errore importazione');
    }
    setUploading(false);
  };

  // Delete import
  const handleDeleteImport = async (importId) => {
    if (!window.confirm('Eliminare questa importazione e i movimenti non riconciliati?')) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/bank-imports/${importId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Importazione eliminata');
        fetchImports();
        fetchAccounts();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore eliminazione');
      }
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  // Delete account
  const handleDeleteAccount = async (accountId) => {
    if (!window.confirm('Disattivare questo conto bancario?')) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/bank-accounts/${accountId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Conto disattivato');
        fetchAccounts();
        setSelectedAccount(null);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore');
    }
  };

  // Helpers
  const resetAccountForm = () => {
    setAccountForm({
      bank_name: '',
      account_number: '',
      iban: '',
      description: '',
      account_type: 'CONTO_CORRENTE',
      opening_balance: 0,
      opening_date: ''
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const canEdit = userRole === 'ADMIN';

  // Access check
  if (userRole === 'RESIDENT') {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 text-red-500" />
              <h2 className="text-xl font-semibold mb-2">Accesso Negato</h2>
              <p className="text-muted-foreground">
                Solo amministratori e contabili possono accedere ai dati bancari.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Banca"
        subtitle="Import estratti conto"
        actions={
          <>
          <ExportButton
            condominiumId={condoId}
            exportType="bank-unmatched"
            title="Non Riconciliati"
            formats={['pdf']}
            showArchive={true}
            token={token}
          />
          {canEdit && (
            <Dialog open={isNewAccountOpen} onOpenChange={setIsNewAccountOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-white/40 text-white hover:bg-white/10">
                  <Plus className="mr-2 h-4 w-4" />
                  Nuovo Conto
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Nuovo Conto Bancario</DialogTitle>
                  <DialogDescription>Aggiungi un nuovo conto bancario</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateAccount} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Banca *</Label>
                      <Input
                        value={accountForm.bank_name}
                        onChange={(e) => setAccountForm({ ...accountForm, bank_name: e.target.value })}
                        placeholder="Es: Intesa Sanpaolo"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Tipo Conto</Label>
                      <Select
                        value={accountForm.account_type}
                        onValueChange={(v) => setAccountForm({ ...accountForm, account_type: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(ACCOUNT_TYPES).map(([k, v]) => (
                            <SelectItem key={k} value={k}>{v}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>IBAN *</Label>
                    <Input
                      value={accountForm.iban}
                      onChange={(e) => setAccountForm({ ...accountForm, iban: e.target.value.toUpperCase() })}
                      placeholder="IT60X0542811101000000123456"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Numero Conto</Label>
                    <Input
                      value={accountForm.account_number}
                      onChange={(e) => setAccountForm({ ...accountForm, account_number: e.target.value })}
                      placeholder="123456789"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Saldo Iniziale</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={accountForm.opening_balance}
                        onChange={(e) => setAccountForm({ ...accountForm, opening_balance: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Data Apertura</Label>
                      <Input
                        type="date"
                        value={accountForm.opening_date}
                        onChange={(e) => setAccountForm({ ...accountForm, opening_date: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Descrizione</Label>
                    <Input
                      value={accountForm.description}
                      onChange={(e) => setAccountForm({ ...accountForm, description: e.target.value })}
                      placeholder="Conto principale condominio"
                    />
                  </div>

                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setIsNewAccountOpen(false)}>
                      Annulla
                    </Button>
                    <Button type="submit">Crea Conto</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}

          {selectedAccount && (
            <Dialog open={isUploadOpen} onOpenChange={setIsUploadOpen}>
              <DialogTrigger asChild>
                <Button className="bg-white text-primary hover:bg-white/90">
                  <Upload className="mr-2 h-4 w-4" />
                  Importa Estratto
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Importa Estratto Conto</DialogTitle>
                  <DialogDescription>
                    Carica l'estratto conto: un file Excel (.xlsx) o CSV, oppure uno o più PDF della banca
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                    <FileSpreadsheet className="h-12 w-12 mx-auto mb-4 text-slate-400" />
                    <Input
                      type="file"
                      accept=".xlsx,.xls,.csv,.pdf,application/pdf"
                      multiple
                      onChange={handleEstrattoSelect}
                      className="max-w-xs mx-auto"
                    />
                    <p className="text-sm text-muted-foreground mt-2">
                      Formati supportati: Excel (.xlsx, .xls), CSV, PDF. Con i PDF si vede prima l'anteprima (non scrive nulla), poi si conferma.
                    </p>
                  </div>

                  {uploadFile && (
                    <div className="bg-slate-50 p-3 rounded-lg flex items-center gap-3">
                      <FileSpreadsheet className="h-8 w-8 text-green-600" />
                      <div>
                        <p className="font-medium">{uploadFile.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {(uploadFile.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="bg-blue-50 p-3 rounded-lg text-sm">
                    <p className="font-medium text-blue-800">Formato file richiesto:</p>
                    <ul className="text-blue-700 mt-1 space-y-1">
                      <li>• Colonna Data (data operazione)</li>
                      <li>• Colonna Descrizione/Causale</li>
                      <li>• Colonna Importo (positivo=entrata, negativo=uscita)</li>
                      <li>• Opzionale: Data Valuta, Riferimento</li>
                    </ul>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsUploadOpen(false)}>
                    Annulla
                  </Button>
                  <Button onClick={handleUpload} disabled={!uploadFile || uploading}>
                    {uploading ? (
                      <>
                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                        Importazione...
                      </>
                    ) : (
                      <>
                        <Upload className="mr-2 h-4 w-4" />
                        Importa
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

          {selectedAccount && (
            <Dialog
              open={isPdfOpen}
              onOpenChange={(aperto) => {
                setIsPdfOpen(aperto);
                if (!aperto) resetPdf();
              }}
            >
              <DialogTrigger asChild>
                <Button variant="outline" className="border-white/40 text-white hover:bg-white/10">
                  <FileText className="mr-2 h-4 w-4" />
                  Importa PDF
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Importa estratti conto PDF</DialogTitle>
                  <DialogDescription>
                    Conto: {selectedAccount.bank_name}. Prima si controlla, poi si importa: l'anteprima non scrive nulla.
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                    <FileText className="h-10 w-10 mx-auto mb-3 text-slate-400" />
                    <Input
                      type="file"
                      accept=".pdf,application/pdf"
                      multiple
                      onChange={handlePdfSelect}
                      className="max-w-xs mx-auto"
                    />
                    <p className="text-sm text-muted-foreground mt-2">
                      Uno o più PDF, fino a {PDF_MAX_FILES} file e 15 MB ciascuno. Anche anni diversi insieme: vengono letti in ordine di data.
                    </p>
                  </div>

                  {pdfFiles.length > 0 && !pdfPreview && (
                    <div className="bg-slate-50 p-3 rounded-lg text-sm">
                      <p className="font-medium mb-1">{pdfFiles.length} file selezionati</p>
                      <ul className="text-muted-foreground space-y-0.5">
                        {pdfFiles.map((f) => (
                          <li key={f.name + f.size}>{f.name} ({(f.size / 1024).toFixed(0)} KB)</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {pdfPreview && !pdfResult && (
                    <div className="space-y-3">
                      <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
                        <span className="font-medium">Anteprima:</span>{' '}
                        {pdfPreview.totale_nuovi} movimenti nuovi, {pdfPreview.totale_duplicati} già presenti, {pdfPreview.file_importabili} file su {pdfPreview.totale_file} importabili.
                        Nessun movimento viene riconciliato in automatico.
                      </div>

                      {pdfPreview.file.map((f) => {
                        const nonQuadra = !f.quadra;
                        return (
                          <div
                            key={f.sha256 + f.nome}
                            className={`rounded-lg border p-3 space-y-2 ${nonQuadra || f.motivo_rifiuto ? 'border-red-300 bg-red-50/40' : 'border-slate-200'}`}
                          >
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <FileText className="h-4 w-4 text-slate-500 shrink-0" />
                                <span className="font-medium truncate">{f.nome}</span>
                              </div>
                              {f.importabile ? (
                                <Badge className="bg-green-100 text-green-800">Pronto da importare</Badge>
                              ) : f.gia_importato ? (
                                <Badge className="bg-yellow-100 text-yellow-800">Già importato</Badge>
                              ) : (
                                <Badge className="bg-red-100 text-red-800">Non importabile</Badge>
                              )}
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                              <div>
                                <p className="text-muted-foreground">Periodo</p>
                                <p className="font-medium">{formatDate(f.periodo_dal)} - {formatDate(f.periodo_al)}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Movimenti</p>
                                <p className="font-medium">
                                  {f.n_movimenti} ({f.n_nuovi} nuovi, {f.n_duplicati} già presenti)
                                </p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Saldo iniziale</p>
                                <p className="font-medium">{f.saldo_iniziale == null ? '-' : formatCurrency(f.saldo_iniziale)}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Saldo finale</p>
                                <p className="font-medium">{f.saldo_finale_dichiarato == null ? '-' : formatCurrency(f.saldo_finale_dichiarato)}</p>
                              </div>
                            </div>

                            <div className={`text-sm rounded p-2 ${f.quadra ? 'bg-green-50 text-green-800' : 'bg-red-100 text-red-800 font-medium'}`}>
                              {f.quadra ? (
                                <span className="flex items-center gap-1">
                                  <CheckCircle className="h-4 w-4" />
                                  Controllo saldi superato: saldo iniziale più movimenti dà il saldo finale dichiarato.
                                </span>
                              ) : (
                                <span className="flex items-start gap-1">
                                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                                  <span>
                                    I saldi non quadrano.
                                    {f.saldo_finale_calcolato != null && f.saldo_finale_dichiarato != null && (
                                      <> Calcolato {formatCurrency(f.saldo_finale_calcolato)}, dichiarato {formatCurrency(f.saldo_finale_dichiarato)}, differenza {formatCurrency(f.saldo_finale_calcolato - f.saldo_finale_dichiarato)}.</>
                                    )}
                                  </span>
                                </span>
                              )}
                            </div>

                            {f.motivo_rifiuto && (
                              <p className="text-sm text-red-700">Motivo: {f.motivo_rifiuto}</p>
                            )}
                            {f.errori && f.errori.length > 0 && (
                              <ul className="text-sm text-red-700 list-disc pl-5">
                                {f.errori.slice(0, 6).map((er, i) => <li key={i}>{er}</li>)}
                                {f.errori.length > 6 && <li>e altri {f.errori.length - 6} segnalazioni</li>}
                              </ul>
                            )}
                            {f.avvisi && f.avvisi.length > 0 && (
                              <ul className="text-sm text-amber-700 list-disc pl-5">
                                {f.avvisi.map((av, i) => <li key={i}>{av}</li>)}
                              </ul>
                            )}

                            {f.movimenti && f.movimenti.length > 0 && (
                              <details className="text-sm">
                                <summary className="cursor-pointer text-muted-foreground">
                                  Mostra i movimenti letti (primi {Math.min(f.movimenti.length, 30)} di {f.movimenti.length})
                                </summary>
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead>Data</TableHead>
                                      <TableHead>Descrizione</TableHead>
                                      <TableHead className="text-right">Importo</TableHead>
                                      <TableHead>Esito</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {f.movimenti.slice(0, 30).map((m, i) => (
                                      <TableRow key={i}>
                                        <TableCell className="whitespace-nowrap">{formatDate(m.data)}</TableCell>
                                        <TableCell className="max-w-[320px] truncate">{m.descrizione}</TableCell>
                                        <TableCell className={`text-right whitespace-nowrap ${m.importo >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                          {formatCurrency(m.importo)}
                                        </TableCell>
                                        <TableCell>
                                          {m.nuovo ? (
                                            <Badge className="bg-green-100 text-green-800">Nuovo</Badge>
                                          ) : (
                                            <Badge variant="outline">Già presente</Badge>
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </details>
                            )}
                          </div>
                        );
                      })}

                      {pdfConForza > 0 && (
                        <label className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800 cursor-pointer">
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={pdfForza}
                            onChange={(e) => setPdfForza(e.target.checked)}
                          />
                          <span>
                            Importa comunque i {pdfConForza} file con i saldi che non quadrano. Sconsigliato: si rischia di
                            caricare movimenti letti male. L'importazione forzata viene registrata nel log.
                          </span>
                        </label>
                      )}
                    </div>
                  )}

                  {pdfResult && (
                    <div className="space-y-2">
                      <div className={`p-3 rounded-lg text-sm ${pdfResult.totale_importati > 0 ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                        <p className="font-medium">
                          Importati {pdfResult.totale_importati} movimenti da {pdfResult.file_importati} file. File rifiutati: {pdfResult.file_rifiutati}.
                        </p>
                        <p>I movimenti importati risultano tutti da riconciliare.</p>
                      </div>
                      <ul className="text-sm space-y-1">
                        {pdfResult.file.map((r) => (
                          <li key={r.sha256 + r.nome} className="flex items-start gap-2">
                            {r.importato ? (
                              <CheckCircle className="h-4 w-4 mt-0.5 text-green-600 shrink-0" />
                            ) : (
                              <XCircle className="h-4 w-4 mt-0.5 text-red-600 shrink-0" />
                            )}
                            <span>
                              <span className="font-medium">{r.nome}</span>:{' '}
                              {r.importato ? `${r.importati} movimenti importati` : (r.motivo_rifiuto || 'non importato')}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsPdfOpen(false)}>
                    {pdfResult ? 'Chiudi' : 'Annulla'}
                  </Button>
                  {!pdfResult && (
                    <Button
                      variant="outline"
                      onClick={handlePdfAnteprima}
                      disabled={!pdfFiles.length || pdfBusy}
                    >
                      {pdfBusy && !pdfPreview ? (
                        <>
                          <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                          Lettura in corso...
                        </>
                      ) : (
                        'Controlla (anteprima)'
                      )}
                    </Button>
                  )}
                  {!pdfResult && (
                    <Button onClick={handlePdfConferma} disabled={!pdfPuoImportare}>
                      {pdfBusy && pdfPreview ? (
                        <>
                          <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                          Importazione...
                        </>
                      ) : (
                        <>
                          <Upload className="mr-2 h-4 w-4" />
                          Importa
                        </>
                      )}
                    </Button>
                  )}
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          </>
        }
      />

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Caricamento...</div>
      ) : accounts.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8 text-muted-foreground">
              <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nessun conto bancario configurato</p>
              {canEdit && (
                <Button className="mt-4" onClick={() => setIsNewAccountOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Aggiungi Conto
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Bank Accounts List */}
          <div className="lg:col-span-1 space-y-4">
            <h2 className="text-lg font-semibold">Conti Bancari</h2>
            {accounts.map((account) => (
              <Card 
                key={account.id}
                className={`cursor-pointer transition-all ${
                  selectedAccount?.id === account.id 
                    ? 'ring-2 ring-slate-900' 
                    : 'hover:shadow-md'
                }`}
                onClick={() => setSelectedAccount(account)}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-base">{account.bank_name}</CardTitle>
                      <CardDescription className="font-mono text-xs">
                        {account.iban}
                      </CardDescription>
                    </div>
                    {canEdit && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteAccount(account.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex justify-between items-center">
                    <Badge variant="outline">{ACCOUNT_TYPES[account.account_type]}</Badge>
                    <span className={`font-semibold ${account.current_balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(account.current_balance)}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Import Files */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>
                  Importazioni - {selectedAccount?.bank_name}
                </CardTitle>
                <CardDescription>
                  Storico file importati per questo conto
                </CardDescription>
              </CardHeader>
              <CardContent>
                {imports.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileSpreadsheet className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Nessuna importazione per questo conto</p>
                    <Button className="mt-4" onClick={() => setIsUploadOpen(true)}>
                      <Upload className="mr-2 h-4 w-4" />
                      Prima Importazione
                    </Button>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>File</TableHead>
                        <TableHead>Data</TableHead>
                        <TableHead>Movimenti</TableHead>
                        <TableHead>Stato</TableHead>
                        <TableHead className="text-right">Azioni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {imports.map((imp) => {
                        const statusInfo = IMPORT_STATUS[imp.status] || IMPORT_STATUS.UPLOADED;
                        const StatusIcon = statusInfo.icon;
                        return (
                          <TableRow key={imp.id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <FileSpreadsheet className="h-4 w-4 text-green-600" />
                                <span className="font-medium truncate max-w-[150px]">{imp.file_name}</span>
                              </div>
                            </TableCell>
                            <TableCell>{formatDate(imp.uploaded_at)}</TableCell>
                            <TableCell>
                              <Badge variant="secondary">{imp.transactions_count}</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge className={statusInfo.color}>
                                <StatusIcon className="h-3 w-3 mr-1" />
                                {statusInfo.label}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              {canEdit && imp.status !== 'PROCESSING' && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDeleteImport(imp.id)}
                                >
                                  <Trash2 className="h-4 w-4 text-red-600" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};
